import {peekCachedProductImageDisplayUrl} from 'utils/productRemoteImageCache';
import {ORDER_SHOP_HOST} from 'utils/whatsappShareUrl';
import JSDB from '../../fromKotlin/nk';

/** Same host the customer-products grid uses for catalog thumbnails. */
export const PRODUCT_IMAGE_GETTER_URL = `${ORDER_SHOP_HOST}:4000/clients/products/image/getter.jpg`;

export type ExcelExportImage = {
    base64: string;
    imageType: 'jpg' | 'png' | 'gif';
};

export function isImageExportColumn(colId?: string | null, headerName?: string | null): boolean {
    const id = String(colId ?? '').toLowerCase();
    const header = String(headerName ?? '').trim();
    return id === 'image' || id.includes('image') || header === 'תמונה';
}

function mimeToImageType(mime: string): ExcelExportImage['imageType'] | null {
    const m = String(mime || '').toLowerCase();
    if (m.includes('png')) return 'png';
    if (m.includes('gif')) return 'gif';
    if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
    return null;
}

export function parseDataUrl(dataUrl: string): ExcelExportImage | null {
    const m = /^data:image\/(png|jpeg|jpg|gif);base64,/i.exec(dataUrl);
    if (!m) return null;
    let imageType = m[1].toLowerCase();
    if (imageType === 'jpeg') imageType = 'jpg';
    if (imageType !== 'png' && imageType !== 'jpg' && imageType !== 'gif') return null;
    return {base64: dataUrl, imageType};
}

async function blobToExcelImage(blob: Blob): Promise<ExcelExportImage | null> {
    if (!blob || blob.size === 0) return null;
    const fromMime = mimeToImageType(blob.type);
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error || new Error('read failed'));
        reader.readAsDataURL(blob);
    });
    const parsed = parseDataUrl(dataUrl);
    if (parsed) return parsed;
    if (!fromMime) return null;
    return {base64: dataUrl, imageType: fromMime};
}

async function fetchUrlAsExcelImage(url: string): Promise<ExcelExportImage | null> {
    // no-store: force-cache can reuse the wrong response across similar getter URLs.
    const attempts: RequestInit[] = [
        {mode: 'cors', credentials: 'omit', cache: 'no-store'},
        {mode: 'cors', credentials: 'include', cache: 'no-store'},
    ];
    for (const init of attempts) {
        try {
            const res = await fetch(url, init);
            if (!res.ok) continue;
            const blob = await res.blob();
            const img = await blobToExcelImage(blob);
            if (img) return img;
        } catch {
            /* try next */
        }
    }
    return null;
}

/**
 * Product id used for the catalog image endpoint — matches grid renderers:
 * customer products → row.id; supplier rows may use connected_products[0].id.
 */
export function productIdForImageRow(row: any): number | null {
    if (!row) return null;
    const connected = row.connected_products?.[0]?.id;
    const rawId = connected != null && Number(connected) > 0 ? connected : row.id;
    const n = Number(rawId);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
}

export function excelImageKeyForRow(row: any): string | null {
    const id = productIdForImageRow(row);
    return id == null ? null : String(id);
}

function buildProductImageRemoteUrl(productId: number): string {
    const company = JSDB().user?.user_company_name ?? '';
    const params = new URLSearchParams({
        company,
        product_id: String(productId),
        jwt: JSDB().getToken() || '',
    });
    return `${PRODUCT_IMAGE_GETTER_URL}?${params.toString()}`;
}

/**
 * Resolve image the same way the grid shows it:
 * 1) inline data: URL on the row
 * 2) blob already cached for that product id (what the user sees)
 * 3) fetch getter.jpg?product_id=… (no HTTP cache)
 */
export async function resolveProductRowImageForExcel(row: any): Promise<ExcelExportImage | null> {
    const productId = productIdForImageRow(row);
    const raw = row?.image;

    if (raw && String(raw).startsWith('data:')) {
        return parseDataUrl(String(raw));
    }

    if (productId != null) {
        const company = JSDB().user?.user_company_name ?? '';
        const cached = peekCachedProductImageDisplayUrl(company, productId);
        if (cached) {
            if (cached.startsWith('data:')) return parseDataUrl(cached);
            const fromCache = await fetchUrlAsExcelImage(cached);
            if (fromCache) return fromCache;
        }
    }

    // Prefer explicit http(s) on the row only when it already includes product_id.
    if (raw && (String(raw).startsWith('http://') || String(raw).startsWith('https://'))) {
        const url = String(raw);
        if (productId == null || url.includes(`product_id=${productId}`) || url.includes(`product_id%3D${productId}`)) {
            const fromRaw = await fetchUrlAsExcelImage(url);
            if (fromRaw) return fromRaw;
        }
    }

    if (productId == null) return null;
    return fetchUrlAsExcelImage(buildProductImageRemoteUrl(productId));
}

async function mapPool<T, R>(items: T[], concurrency: number, fn: (item: T, index: number) => Promise<R>): Promise<R[]> {
    const results = new Array<R>(items.length);
    let next = 0;
    const workers = Array.from({length: Math.min(Math.max(1, concurrency), Math.max(1, items.length))}, async () => {
        while (next < items.length) {
            const idx = next++;
            results[idx] = await fn(items[idx], idx);
        }
    });
    await Promise.all(workers);
    return results;
}

/**
 * Prefetch Excel images. Keys are String(productId).
 * Fetch sequentially per key; never share one response across products.
 */
export async function buildExcelImageMapByProductId(
    rows: any[],
    concurrency = 4
): Promise<Map<string, ExcelExportImage>> {
    const unique = new Map<string, any>();
    for (const row of rows || []) {
        const key = excelImageKeyForRow(row);
        if (!key) continue;
        if (!unique.has(key)) unique.set(key, row);
    }
    const entries = Array.from(unique.entries());
    const map = new Map<string, ExcelExportImage>();
    await mapPool(entries, concurrency, async ([key, row]) => {
        const img = await resolveProductRowImageForExcel(row);
        if (img) map.set(key, img);
        return null;
    });
    return map;
}
